import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Bookmark, HeartIcon, MessageCircle, SendIcon, Share2, XIcon } from 'lucide-react-native';
import {
  forwardRef,
  type LegacyRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Keyboard,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  TextInput,
  type TextInputFocusEventData,
  type TextInputProps,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

import { COMMENT_COUNT_FROM, COMMENT_MAX_LENGTH } from '../../model/commentLimit';

import { AnonymousHatIcon } from '@/components/AnonymousHatIcon';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { LibraryPost } from '@/features/inquiry-library/model';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { useUserScope } from '@/hooks/useUserScope';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { toNumericUserId } from '@/util/authTokens';

export type CommentBoxHandle = {
  focus: () => void;
};

/**
 * The like, share and save icons at rest, drawn while the post loads.
 *
 * The comment bar stays on screen through every state of the post, so the
 * field keeps its width: without these the input would stretch across the
 * row and then shrink when the real reactions arrived. They are the same
 * icons, sizes and gaps as the action bar's, and inert until then.
 */
const RestingReactions = ({ withComments = false }: { withComments?: boolean }) => {
  const { colors } = useColors();

  return (
    <HStack className="items-center gap-5" aria-hidden>
      {withComments ? (
        <View className="min-h-10 min-w-10 flex-row items-center justify-start">
          <MessageCircle color={colors.primary[600]} size={24} />
        </View>
      ) : null}

      <View className="min-h-10 min-w-10 flex-row items-center justify-start">
        <HeartIcon color={colors.primary[600]} size={24} fill={colors.transparent} />
      </View>

      <View className="min-h-10 min-w-10 flex-row items-center justify-center">
        <Share2 color={colors.primary[600]} size={24} fill={colors.transparent} />
      </View>

      <View className="min-h-10 min-w-10 flex-row items-center justify-center">
        <Bookmark color={colors.primary[600]} size={24} fill={colors.transparent} />
      </View>
    </HStack>
  );
};

export const CommentBox = forwardRef<
  CommentBoxHandle,
  {
    disabled?: boolean;
    data?: LibraryPost;
    /**
     * The post's like, save and share controls, drawn in the composer once the
     * post is in. The screen passes them in so comments never import the
     * Inquiry Library, which renders comments.
     */
    reactions?: ReactNode;
    /**
     * The jump-to-comments control, drawn before the reactions in the collapsed web bar at md and
     * wider. Passed in for the same reason as `reactions`.
     */
    commentsLink?: ReactNode;
    replyToUser?: { firstName: string; lastName: string } | null;
    onSubmit?: ({
      body,
      isAnonymous,
    }: {
      body: string;
      isAnonymous: boolean;
    }) => Promise<boolean | void>;
    isSubmitting?: boolean;
    onCancel?: () => void;
    isReply?: boolean;
    isTopLevelReply?: boolean;
  }
>(
  (
    {
      disabled = false,
      data,
      reactions,
      commentsLink,
      replyToUser,
      onSubmit,
      onCancel,
      isReply = false,
      isTopLevelReply = false,
    },
    ref
  ) => {
    const { bottom } = useSafeAreaInsets();

    const [comment, setComment] = useState('');

    const [isAnonymous, setIsAnonymous] = useState(false);

    const composerToast = useToaster();

    const { colors: composerColors } = useColors();

    /**
     * The toggle shows only the hat, so turning it on says in words what it does. Turning it off
     * stays silent: the hat losing its tint is the confirmation, and the next comment shows a name.
     */
    const handleAnonymityChange = (next: boolean) => {
      setIsAnonymous(next);

      if (next) {
        composerToast.showInfo({
          title: isReply
            ? 'Your reply will be posted anonymously'
            : 'Your comment will be posted anonymously',
          // The same filled hat in the same color as the toggle it confirms.
          iconElement: (
            <AnonymousHatIcon
              color={composerColors.primary[600]}
              surface={composerColors.background[0]}
              filled
              size={20}
            />
          ),
          liveRegion: 'polite',
          duration: 4000,
        });
      }
    };

    const inputRef = useRef<TextInput>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    const [isFocused, setIsFocused] = useState(false);

    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    const { userId: currentUserId } = useUserScope();

    const userId = toNumericUserId(currentUserId);

    const { guard, requestAccess } = useAbilityGate();

    const [height, setHeight] = useState(0);

    const lastHeightRef = useRef(0);

    const debouncedSetHeight = useDebouncedCallback((newHeight: number) => {
      setHeight(newHeight);
    }, 0);

    const updateHeight = (newHeight: number) => {
      if (newHeight === lastHeightRef.current) {
        return;
      }

      lastHeightRef.current = newHeight;

      if (isWeb) {
        debouncedSetHeight(newHeight);

        return;
      }

      setHeight(newHeight);
    };

    useEffect(() => {
      setComment('');

      setIsAnonymous(false);
    }, [replyToUser]);

    useEffect(() => {
      if (Platform.OS === 'web') {
        return;
      }

      const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';

      const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

      const handleKeyboardVisibilityShow = () => setIsKeyboardVisible(true);

      const handleKeyboardVisibilityHide = () => setIsKeyboardVisible(false);

      const showSub = Keyboard.addListener(showEvent, handleKeyboardVisibilityShow);

      const hideSub = Keyboard.addListener(hideEvent, handleKeyboardVisibilityHide);

      return () => {
        showSub.remove();

        hideSub.remove();
      };
    }, []);

    /**
     * On web the actions ride in the collapsed row, so the separate action row
     * never renders: the bar is one row at rest and one row while typing, and
     * focusing swaps the prompt for the field in the same place rather than
     * moving everything.
     *
     * Native keeps its own rule, where the keyboard drives the height and the
     * actions hide as soon as the field takes focus.
     */
    const showActionBar = isWeb ? false : !isKeyboardVisible && !isFocused && !replyToUser;

    /**
     * The pinned bar opens as a prompt rather than as a field.
     *
     * It is two rows at rest otherwise, an action row above a full input, for
     * a reader who is almost always reading rather than writing. Collapsed it
     * is one row, and the first press puts the real field in the same place.
     *
     * Web only. On native the keyboard already drives the bar's height, and
     * `showActionBar` there already hides the actions the moment the field has
     * focus.
     */
    const collapsed =
      isWeb && !isReply && !replyToUser && !isFocused && comment.trim().length === 0;

    /**
     * The anonymity choice, drawn once and placed by width.
     *
     * On a phone it sits in the row above the field, on the right, with the
     * reactions on the left, so the field keeps the full width to write in; it
     * used to share the field's row and took nearly half of it. It stays in
     * that row while the reader types, when the reactions hide, because the
     * choice has to be visible before send. On desktop web it keeps its place
     * beside send.
     */
    /**
     * Native keeps the toggle in the action row while the reactions show, so it sits with the
     * heart, share and bookmark at rest. While the reader types, or in an inline reply, the
     * reactions hide and that row would hold one icon above a blank strip, so the toggle moves
     * into the field's row instead, at the same right edge, and the strip goes.
     */
    const nativeToggleInActionRow = showActionBar && !isReply;

    const renderAnonymityToggle = (labeled: boolean) => (
      /* The design system's Checkbox, drawn as a pill.
        It was a plain Pressable with role="button" and a `selected`
        state, which is the wrong semantics for a yes/no choice about
        whether a reader's name appears: a screen reader should hear a
        checkbox and its checked state, and Space should toggle it.
        `getAccessibilityProps` has no checkbox role, so the control
        itself has to be one, restyled rather than reimplemented.
        The indicator box is dropped because the pill's own tint and
        border carry the state, and a box beside a hat would say it
        twice. The resting border is outline-500 rather than a lighter
        stop: it is what tells a reader where the control begins, so it
        is held to the 3:1 non-text threshold, and outline-500 is the
        lightest stop that clears it on white. The shape is the other
        half of the distinction: round for a mode, rounded-rect for the
        field and the action that belongs to it. */
      <Checkbox
        value="anonymous"
        onChange={handleAnonymityChange}
        isChecked={isAnonymous}
        size="sm"
        hitSlop={getAccessibleHitSlop(40)}
        aria-label={isReply ? 'Reply anonymously' : 'Comment anonymously'}
        className={twJoin(
          'h-10 flex-none items-center justify-center active:opacity-70',
          labeled ? 'shrink-0 flex-row gap-2 px-2' : 'w-10'
        )}>
        {/* A step larger than the send icon's 16px, not equal to it.
          The hat is a wide, low glyph with fine internal detail and
          the send arrow is a solid diagonal filling its box, so at a
          matching 16 the hat reads smaller than it measures. */}
        {/* Filled when on, like the heart and the bookmark beside it, in the same color. No circle
          in either state, and the toast says what it means. */}
        <AnonymousHatIcon
          color={composerColors.primary[600]}
          surface={composerColors.background[0]}
          filled={isAnonymous}
        />
        {/* Wide web only: the words are the accessible name already, so the visible text matches
          it and the label in the name holds. It takes the filled hat's tint when on. */}
        {labeled ? (
          <Text
            className={twJoin(
              'text-sm',
              isAnonymous ? 'font-medium text-primary-600' : 'text-typography-600'
            )}>
            {isReply ? 'Reply anonymously' : 'Comment anonymously'}
          </Text>
        ) : null}
      </Checkbox>
    );

    const anonymityToggle = renderAnonymityToggle(false);

    /**
     * The reactions on the left of native's row above the field: live once the
     * post is in, at rest while it loads, and gone while the reader types or in
     * an inline reply, leaving the anonymity toggle on its own.
     */
    const renderNativeReactions = () => {
      if (!showActionBar || isReply) {
        return <View />;
      }

      if (!data) {
        return <RestingReactions />;
      }

      return reactions ?? null;
    };

    /** Web's reactions: live once the post is in, at rest while it loads. */
    const webReactions = data ? reactions : <RestingReactions />;

    /**
     * The collapsed web bar at md and wider reads: field, comments, then the reactions. The
     * comments control has its own slot so the phone row and the expanded bar are untouched.
     */
    const wideCollapsedActions = data ? (
      <HStack className="items-center gap-5">
        {commentsLink}
        {reactions}
      </HStack>
    ) : (
      <RestingReactions withComments={Boolean(commentsLink)} />
    );

    /**
     * Web only: a mouse press on the anonymity toggle or send must not take focus from the field.
     *
     * The toggle is a label, which is not focusable, so pressing it moves focus to the body with a
     * null `relatedTarget` and the field's blur guard cannot tell the press came from the bar. The
     * bar collapsed and unmounted the toggle before the click finished. Canceling the default of
     * `mousedown` keeps focus in the field, and the click still fires. Keyboard focus is untouched.
     */
    const keepFieldFocused = isWeb
      ? { onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() }
      : {};

    const expand = () => {
      setIsFocused(true);

      requestAnimationFrame(() => inputRef.current?.focus());
    };

    /**
     * Sends the draft and, once it went, empties the field and closes the bar. A caller that
     * could not send resolves `false`, and the draft, the anonymity choice and the open bar
     * stay as they were so the reader can try again.
     */
    const sendComment = async () => {
      const sent = await onSubmit?.({ body: comment, isAnonymous });

      if (sent === false) {
        return;
      }

      setComment('');

      setIsAnonymous(false);

      setIsFocused(false);

      inputRef.current?.blur();

      Keyboard.dismiss();
    };

    const ActionButtons = () => {
      if (!isWeb) {
        return null;
      }

      if (replyToUser) {
        return (
          <>
            <Button
              variant="outline"
              size="sm"
              onPress={(e) => {
                e?.stopPropagation();

                setIsFocused(false);

                if (!userId) {
                  requestAccess();

                  return;
                }

                if (onCancel) {
                  onCancel();

                  return;
                }

                setComment('');

                setIsAnonymous(false);

                inputRef.current?.blur();

                Keyboard.dismiss();
              }}>
              <ButtonIcon as={XIcon} size="sm" />
            </Button>
            <Button
              size="sm"
              accessibilityLabel="Send comment"
              isDisabled={!comment.trim()}
              onPress={async (e) => {
                e?.stopPropagation();

                if (!guard('create', 'Comment')) {
                  return;
                }

                if (!comment.trim()) {
                  return;
                }

                await sendComment();
              }}>
              <ButtonIcon as={SendIcon} size="sm" />
            </Button>
          </>
        );
      }

      return (
        <Button
          size="sm"
          accessibilityLabel="Send comment"
          isDisabled={!comment.trim()}
          onPress={async (e) => {
            e?.stopPropagation();

            if (!guard('create', 'Comment')) {
              return;
            }

            if (!comment.trim()) {
              return;
            }

            await sendComment();
          }}>
          <ButtonIcon as={SendIcon} size="sm" />
        </Button>
      );
    };

    useEffect(() => {
      if (replyToUser && inputRef.current) {
        inputRef.current.focus();
      }
    }, [replyToUser]);

    /**
     * A hairline separates the pinned bar from the post, where a drop shadow
     * used to.
     *
     * The shadow was `0px -2px 3px rgba(0, 0, 0, 0.1)`, written by hand and
     * matching no token in `tailwind.config.js` (the upward one there is
     * `shadow-hard-4`). A 1px rule does the same job, reads lighter, and is a
     * value the design system owns.
     */
    const containerStyle = {
      paddingBottom: bottom,
    };

    const separated = !isReply || (!isWeb && isReply);

    /**
     * On web the bar's background and rule run edge to edge while its content sits in the post's
     * 768px reading column, so the field and icons share the column's edges. Replies and native
     * keep their own box.
     */
    const isColumnBar = isWeb && !isReply;

    const barSurface = twJoin('bg-background-0', separated ? 'border-t border-outline-200' : '');

    return (
      <View style={containerStyle} className={isColumnBar ? barSurface : undefined}>
        <VStack
          className={twJoin(
            isWeb ? 'gap-2' : 'gap-1',
            isColumnBar ? '' : barSurface,
            isColumnBar ? 'mx-auto w-full max-w-3xl' : '',
            isReply ? 'mb-6' : '',
            /**
             * `pb-3` rather than `pb-8`. The 32px sat on top of the safe-area
             * inset the container already applies, which is zero on web, so it
             * was dead space under the input on every desktop post page.
             */
            !isReply
              ? twJoin(isColumnBar ? 'px-4 md:px-6' : 'px-5', 'pb-3', isWeb ? 'pt-3' : 'pt-1')
              : '',
            isTopLevelReply ? 'ml-12' : '',
            !isTopLevelReply && isReply ? 'ml-24' : ''
          )}>
          {/* Native's action row, above the field. Drawn at rest while the post
              loads so the row is there from the start instead of pushing the
              field down when the post lands; the count lives on the comments
              heading, not here. */}
          {!isWeb && nativeToggleInActionRow && (
            <HStack className="items-center justify-between gap-3">
              {renderNativeReactions()}
              {anonymityToggle}
            </HStack>
          )}
          {/* Web below md takes native's layout: reactions and the toggle in
              a row above, the field full width beneath. The reactions only
              rest here while the bar is collapsed, like native hiding them
              while typing. */}
          {isWeb && !isReply && (
            <HStack className="items-center justify-between gap-3 md:hidden" {...keepFieldFocused}>
              {collapsed ? webReactions : <View />}
              {anonymityToggle}
            </HStack>
          )}
          {collapsed && (
            <HStack className="items-center gap-3">
              {/* No comment count here: it heads the comments themselves, so
                  nothing in this bar depends on the post and it never shifts
                  as the post loads. The row reads: write, then react. */}
              {/* Drawn as the field at rest, not as a pill.
                  It stands in for the input, so pressing it should look like
                  arriving in the field rather than swapping one control for
                  another: a pill differed in height, radius, border, fill and
                  padding all at once, and the change read as a different
                  control appearing. Matched to the `Input` below, the press
                  adds a focus ring and a cursor and nothing else moves.
                  36px tall, so the slop is computed from that rather than from
                  the 40px icon buttons. */}
              {/* Inert until the post arrives, so nobody starts a comment on a
                  post they cannot read yet. */}
              <Pressable
                onPress={expand}
                disabled={!data}
                accessibilityState={{ disabled: !data }}
                hitSlop={getAccessibleHitSlop(36)}
                accessibilityRole="button"
                accessibilityLabel="Add your thoughts"
                className={twJoin(
                  'h-9 flex-1 justify-center rounded border border-background-300 bg-background-0 px-2',
                  data && 'web:cursor-pointer web:hover:border-background-400'
                )}>
                <Text className="text-base text-typography-600">Add your thoughts...</Text>
              </Pressable>
              {/* Only on web: on a phone the hidden view still took a gap's width, so the
                  field stopped short of the toggle's edge above it. */}
              {isWeb ? <View className="hidden md:flex">{wideCollapsedActions}</View> : null}
            </HStack>
          )}
          {replyToUser && (
            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <Text className="text-sm text-typography-700">
                  Replying to{' '}
                  <Text className="font-medium text-sm text-typography-900">
                    @
                    {replyToUser.firstName?.trim() && replyToUser.lastName?.trim()
                      ? `${replyToUser.firstName} ${replyToUser.lastName}`
                      : 'Anonymous user'}
                  </Text>
                </Text>
              </HStack>
              {!isWeb && (
                <Pressable
                  onPress={() => {
                    onCancel?.();

                    setIsAnonymous(false);

                    setIsFocused(false);

                    Keyboard.dismiss();
                  }}
                  className="h-8 w-8 items-center justify-center rounded-full"
                  /* Sized from the control it is on: 32px needs 8 a side to
                     reach the 48px minimum, where the 40px icon buttons need
                     4. The shared constant is the 40px one, so using it here
                     would shrink this target rather than grow it. */
                  hitSlop={getAccessibleHitSlop(32)}>
                  {({ pressed }) => (
                    <Icon
                      as={XIcon}
                      size="lg"
                      className={pressed ? 'text-typography-300' : 'text-primary-500'}
                    />
                  )}
                </Pressable>
              )}
            </HStack>
          )}
          <HStack
            className={twJoin('items-end gap-3', collapsed && 'hidden')}
            style={{ pointerEvents: disabled ? 'none' : 'auto' }}>
            <Input
              className={twJoin(
                'text-md relative h-9 flex-1 border border-background-300 bg-background-0',
                isFocused && 'border-primary-500'
              )}
              style={{ minHeight: height }}>
              <InputField
                className={twJoin(
                  isWeb ? 'h-full' : 'h-auto',
                  isWeb ? 'font-normal' : '',
                  isWeb ? 'px-2 py-0' : 'py-0',
                  'content-center bg-background-0',
                  isFocused && 'border-primary-500'
                )}
                multiline
                maxLength={COMMENT_MAX_LENGTH}
                value={comment}
                onChangeText={setComment}
                onContentSizeChange={(e) => {
                  const contentHeight = e.nativeEvent.contentSize.height;

                  const newHeight = Math.min(
                    Math.max(16, isWeb ? contentHeight : contentHeight + 16),
                    192
                  );

                  updateHeight(newHeight);
                }}
                ref={inputRef as LegacyRef<TextInputProps>}
                placeholder="Add your thoughts..."
                editable={!disabled}
                onFocus={(e: NativeSyntheticEvent<TextInputFocusEventData>) => {
                  e?.stopPropagation();

                  if (!guard('create', 'Comment')) {
                    return;
                  }

                  setIsFocused(true);
                }}
                onBlur={(e: NativeSyntheticEvent<TextInputFocusEventData>) => {
                  if (isWeb) {
                    const target = (e.nativeEvent as unknown as FocusEvent).relatedTarget;

                    /* The bar's own controls take focus away from the field, and
                       collapsing on that would discard the choice the reader just
                       made. `label` is here for the anonymity checkbox: clicking
                       its pill moves focus to the hidden input the label wraps,
                       which is not a button and would otherwise close the bar. */
                    if (!target || !(target as HTMLElement).closest('button, label')) {
                      setIsFocused(false);
                    }
                  } else {
                    setIsFocused(false);
                  }
                }}
              />
              {!isWeb && (
                <InputSlot
                  accessibilityLabel="Send comment"
                  className="absolute right-2 flex h-full items-center justify-end bg-background-0 pb-2"
                  onPress={async (e) => {
                    e?.stopPropagation();

                    if (!guard('create', 'Comment')) {
                      return;
                    }

                    if (!comment.trim()) {
                      return;
                    }

                    await sendComment();
                  }}>
                  <InputIcon
                    as={SendIcon}
                    className={twJoin(
                      comment.trim().length > 0 ? 'text-primary-500' : 'text-typography-300'
                    )}
                  />
                </InputSlot>
              )}
            </Input>
            {/* The toggle sits with send, tighter to it than to the field, so
                proximity says it modifies what send does.
                It was on the left for a while, on the reasoning that a mode is
                chosen before writing. That reads well and costs the field a
                stable left edge: the collapsed row opens with the comment
                action, so with nothing in that slot the field shifts when the
                bar expands. Accepted here as the smaller problem. */}
            {/* Web only: on a phone the toggle rides in the row above and there are no
                action buttons, and an empty HStack here still took the row's 12px gap, so
                the field stopped short of the toggle's edge above it. */}
            {isWeb ? (
              <HStack className="items-end gap-2" {...keepFieldFocused}>
                <View className="hidden shrink-0 md:flex">{renderAnonymityToggle(true)}</View>
                <ActionButtons />
              </HStack>
            ) : null}
            {!isWeb && !nativeToggleInActionRow ? anonymityToggle : null}
          </HStack>
          {comment.length >= COMMENT_COUNT_FROM ? (
            <Text className="self-end text-xs text-typography-700" accessibilityLiveRegion="polite">
              {`${comment.length.toLocaleString('en-US')} / ${COMMENT_MAX_LENGTH.toLocaleString('en-US')}`}
            </Text>
          ) : null}
        </VStack>
      </View>
    );
  }
);

CommentBox.displayName = 'CommentBox';
